'use client'

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { videoApi } from '@/app/lib/api'

interface VideoThumbnailProps {
  videoId: number | null | undefined
  thumbnailUrl: string | undefined
  alt: string
  className?: string
}

/**
 * VideoThumbnail component that displays a thumbnail from Meilisearch data
 * or fetches it from the video API if not available.
 * Uses React Query for caching to reduce unnecessary API calls.
 */
export default function VideoThumbnail({ 
  videoId, 
  thumbnailUrl, 
  alt, 
  className = '' 
}: VideoThumbnailProps) {
  const [imageError, setImageError] = useState(false)
  
  const { data: fetchedThumbnail, isLoading, isError } = useQuery({
    queryKey: ['video-thumbnail', videoId],
    queryFn: async () => {
      const response = await videoApi.getPlaybackUrls(videoId!)
      if (response.success && response.data?.thumbnail_url) {
        return response.data.thumbnail_url
      }
      throw new Error('No thumbnail found')
    },
    enabled: !thumbnailUrl && !!videoId && !isNaN(Number(videoId)),
    staleTime: 24 * 60 * 60 * 1000,  // 24 hours
    gcTime: 48 * 60 * 60 * 1000,     // 48 hours
    retry: 1,
  })

  const thumbnail = thumbnailUrl || fetchedThumbnail
  const loading = !thumbnailUrl && isLoading
  const error = !thumbnailUrl && isError

  if (loading) {
    return (
      <div className={`bg-secondary animate-pulse flex items-center justify-center ${className}`}>
        <svg
          className="w-8 h-8 text-muted-foreground opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    )
  }

  if (thumbnail && !error && !imageError) {
    return (
      <img
        src={thumbnail}
        alt={alt}
        className={className}
        width={320}
        height={180}
        loading="lazy"
        onError={() => setImageError(true)}
      />
    )
  }

  // Fallback placeholder
  return (
    <div className={`bg-secondary flex items-center justify-center ${className}`}>
      <svg
        className="w-12 h-12 text-muted-foreground"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </div>
  )
}
